import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './TabBar.css';

function TabBar({
  tabs,
  activeTab,
  onTabChange,
  toolbar = null,
  className = '',
  onEscapePress = null,
  level = 2, // Navigation level: 1 = main pages, 2 = tabs, 3 = subtabs
  parentTab = null, // Parent tab for subtabs
  tabRefs = null // Reference array for keyboard navigation
}) {
  const { t } = useTranslation('common');
  const tabsRef = useRef(null);
  const isTabBarFocused = useRef(false);
  const navigationLevel = useRef(level);
  const tabButtonRefs = useRef([]);
  const initTimeRef = useRef(Date.now());

  // Add keyboard shortcuts and arrow navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Check if a grid or other focusable element has focus
      const focusedElement = document.activeElement;
      const isGridFocused = focusedElement && focusedElement.classList.contains('grid-table-container');

      // Determine if this TabBar should handle navigation based on level
      const shouldHandleNavigation = () => {
        // Check if there's a deeper level TabBar focused
        const allTabBars = document.querySelectorAll('.tabs-container');
        let deepestLevel = navigationLevel.current;

        allTabBars.forEach(bar => {
          const barLevel = parseInt(bar.dataset.level || '2');
          if (bar.contains(focusedElement) && barLevel > deepestLevel) {
            deepestLevel = barLevel;
          }
        });

        return deepestLevel === navigationLevel.current;
      };

      // Handle arrow keys for tab navigation when appropriate
      if ((!isGridFocused || isTabBarFocused.current) && shouldHandleNavigation()) {
        const currentIndex = tabs.findIndex(t => t.id === activeTab);

        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          e.stopPropagation();
          // Move to previous tab
          let prevIndex = currentIndex - 1;
          while (prevIndex >= 0 && tabs[prevIndex].disabled) {
            prevIndex--;
          }
          if (prevIndex >= 0) {
            onTabChange(tabs[prevIndex].id);
            isTabBarFocused.current = true;
          }
          return;
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          e.stopPropagation();
          // Move to next tab
          let nextIndex = currentIndex + 1;
          while (nextIndex < tabs.length && tabs[nextIndex].disabled) {
            nextIndex++;
          }
          if (nextIndex < tabs.length) {
            onTabChange(tabs[nextIndex].id);
            isTabBarFocused.current = true;
          }
          return;
        } else if (e.key === 'Escape') {
          // ESC behavior depends on navigation level
          if (navigationLevel.current === 3) {
            // From subtabs (level 3), go back to parent tab (level 2)
            e.preventDefault();
            e.stopPropagation();
            if (onEscapePress) {
              onEscapePress();
            }
            return;
          } else if (navigationLevel.current === 2 && onEscapePress) {
            // From tabs (level 2), call the escape handler
            e.preventDefault();
            e.stopPropagation();
            onEscapePress();
            return;
          }
        } else if (e.key === 'Tab') {
          // Tab key moves focus to tab bar
          if (!isTabBarFocused.current && tabsRef.current) {
            e.preventDefault();
            isTabBarFocused.current = true;
            const activeTabButton = tabsRef.current.querySelector('.tab-button.active');
            if (activeTabButton) {
              activeTabButton.focus();
            }
            return;
          }
        }
      }

      // Handle shortcut keys (but not during initialization)
      const key = e.key.toLowerCase();
      const tab = tabs.find(t => t.shortcut === key);

      if (tab && !tab.disabled) {
        // Prevent shortcuts during component initialization
        const timeSinceInit = Date.now() - initTimeRef.current;
        if (timeSinceInit > 1000) { // Only allow shortcuts after 1 second
          e.preventDefault();
          onTabChange(tab.id);
          isTabBarFocused.current = true;
        }
        // Silently block shortcuts during initialization
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [tabs, activeTab, onTabChange, onEscapePress]);

  // Store tab button references
  useEffect(() => {
    if (tabRefs && tabButtonRefs.current.length > 0) {
      tabRefs.current = tabButtonRefs.current.filter(ref => ref != null);
    }
  }, [tabs, tabRefs]);

  // Handle blur to reset tab bar focus state
  const handleTabBlur = (e) => {
    // Check if focus is leaving the tab bar entirely
    if (!tabsRef.current || !tabsRef.current.contains(e.relatedTarget)) {
      isTabBarFocused.current = false;
    }
  };

  return (
    <div className={`tabs-container ${className}`} ref={tabsRef} data-level={level}>
      <div className="main-tabs">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            ref={el => tabButtonRefs.current[index] = el}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
            onClick={() => {
              if (!tab.disabled) {
                onTabChange(tab.id);
                isTabBarFocused.current = true;
              }
            }}
            onFocus={() => { isTabBarFocused.current = true; }}
            onBlur={handleTabBlur}
            disabled={tab.disabled}
            title={tab.disabled ? t('tabBar.notImplemented') : ''}
            tabIndex={activeTab === tab.id ? 0 : -1}
          >
            {tab.label}
          </button>
        ))}
        {toolbar && <div className="tabs-toolbar">{toolbar}</div>}
      </div>
    </div>
  );
}

export default TabBar;